import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { BookRow } from "@/components/BookRow";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";

export default function Books() {
  const [searchParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [tags, setTags] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [etiket, setEtiket] = useState(searchParams.get("etiket") || "hepsi");
  const [zorluk, setZorluk] = useState("hepsi");
  const [sort, setSort] = useState("yeni");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/books/tags").then((r) => setTags(r.data)); }, []);
  useEffect(() => {
    setQ(searchParams.get("q") || "");
    setEtiket(searchParams.get("etiket") || "hepsi");
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { sort };
    if (q) params.q = q;
    if (etiket !== "hepsi") params.etiket = etiket;
    if (zorluk !== "hepsi") params.zorluk = zorluk;
    const { data } = await api.get("/books", { params });
    setBooks(data);
    setLoading(false);
  }, [q, etiket, zorluk, sort]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="fade-up">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra">Kütüphane</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mt-1">Kitaplar</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mt-8 sticky top-16 z-30 bg-page/90 backdrop-blur-sm py-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input data-testid="book-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Kitap, yazar, etiket veya içerik ara..." className="pl-10 h-11 bg-surface" />
        </div>
        <Select value={etiket} onValueChange={setEtiket}>
          <SelectTrigger className="w-full lg:w-44 h-11 bg-surface" data-testid="filter-tag"><SelectValue placeholder="Etiket" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hepsi">Tüm Etiketler</SelectItem>
            {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={zorluk} onValueChange={setZorluk}>
          <SelectTrigger className="w-full lg:w-40 h-11 bg-surface" data-testid="filter-difficulty"><SelectValue placeholder="Zorluk" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hepsi">Tüm Seviyeler</SelectItem>
            <SelectItem value="Başlangıç">Başlangıç</SelectItem>
            <SelectItem value="Orta">Orta</SelectItem>
            <SelectItem value="İleri">İleri</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-full lg:w-40 h-11 bg-surface" data-testid="filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="yeni">En Yeni</SelectItem>
            <SelectItem value="puan">En Yüksek Puan</SelectItem>
            <SelectItem value="populer">En Popüler</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-muted-foreground mt-8">Yükleniyor...</p>
      ) : books.length === 0 ? (
        <p className="text-muted-foreground mt-8" data-testid="no-books">Aramanıza uygun kitap bulunamadı.</p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mt-4" data-testid="books-count">
            <span className="font-medium text-ink">{books.length}</span> kitap listeleniyor
          </p>
          <div className="mt-3 space-y-3" data-testid="books-grid">
            {books.map((b) => <BookRow key={b.id} book={b} />)}
          </div>
        </>
      )}
    </div>
  );
}
